/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Grid, Alert, Chip } from '@mui/material';
import { Extension, Settings as SettingsIcon } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useAuth } from 'auth/Auth';
import { MAX_OBJECTS } from 'components/SettingsComponents/customObjectsFieldTypes';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';

export default function CustomObjectsList() {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [objects, setObjects] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [objRes, statsRes] = await Promise.all([
          axiosInstance.get('/custom-objects'),
          axiosInstance.get('/custom-objects/statistics'),
        ]);
        setObjects(objRes.data || []);
        setStats(statsRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [axiosInstance]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ p: 3, mb: 3, background: 'linear-gradient(to right, #1565C8, #5B9BD5)' }}>
          <MDTypography variant="h4" color="white" fontWeight="medium">Custom Objects</MDTypography>
          <MDTypography variant="body2" color="white" opacity={0.85}>
            Manage records for your tenant-defined modules.
          </MDTypography>
          {stats && (
            <MDBox display="flex" gap={1} mt={2} flexWrap="wrap">
              <Chip label={`${stats.total_objects} objects`} sx={{ bgcolor: 'white' }} />
              <Chip label={`${stats.total_records} records`} sx={{ bgcolor: 'white' }} />
              <Chip label={`${stats.total_objects} / ${MAX_OBJECTS} limit`} sx={{ bgcolor: 'white' }} />
            </MDBox>
          )}
        </Card>

        {loading ? (
          <DataLoadingSpinner
            entityType="default"
            title="Loading Custom Objects"
            message="Please wait while we fetch your custom objects..."
          />
        ) : (
          <Grid container spacing={3}>
            {objects.map((obj) => {
              const objStats = stats?.object_stats?.[obj.apiName] || {};
              return (
                <Grid item xs={12} md={4} key={obj.id}>
                  <Card sx={{ p: 2, height: '100%' }}>
                    <MDBox display="flex" alignItems="center" mb={1}>
                      <Extension color="info" sx={{ mr: 1 }} />
                      <MDTypography variant="h6">{obj.pluralName}</MDTypography>
                    </MDBox>
                    <MDTypography variant="body2" color="text" mb={2}>{obj.description || 'Custom object module'}</MDTypography>
                    <Chip size="small" label={`${obj.totalFields || 0} fields`} sx={{ mr: 1 }} />
                    <Chip size="small" label={`${objStats.count || 0} records`} color="info" variant="outlined" />
                    <MDBox mt={2} display="flex" gap={1}>
                      <MDButton color="info" size="small" onClick={() => navigate(`/custom-objects/${obj.apiName}`)}>Open</MDButton>
                      <MDButton color="secondary" size="small" onClick={() => navigate('/settings/custom-objects')}>
                        <SettingsIcon fontSize="small" sx={{ mr: 0.5 }} /> Settings
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
              );
            })}
            {!objects.length && (
              <Grid item xs={12}>
                <Alert severity="info">
                  No custom objects yet.{' '}
                  <MDButton variant="text" color="info" onClick={() => navigate('/settings/custom-objects')}>
                    Create one in Settings
                  </MDButton>
                </Alert>
              </Grid>
            )}
          </Grid>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
